import {
  File02Icon,
  InfoCircleIcon,
  Key01Icon,
} from '@langchain/untitled-ui-icons';
import LinearProgress from '@mui/joy/LinearProgress';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CreateKeyModal } from '@/Pages/Settings/components/CreateKeyModal.tsx';
import { Badge } from '@/design-system';
import { Button } from '@/design-system/components/Button/Button';
import { GroupedTabs } from '@/design-system/components/GroupedTabs/GroupedTabs';
import { useAuth } from '@/hooks/useAuth/useAuth';
import { usePermissions } from '@/hooks/usePermissions.tsx';
import {
  useCurrentOrganization,
  useOrgScopedApiKeys,
  useOrgScopedPatKeys,
  useOrganizationId,
} from '@/hooks/useSwr';
import TrashIcon from '@/icons/TrashIcon.svg?react';
import { ApiKeySchema } from '@/types/schema';
import {
  appOrganizationPath,
  appSettingsPath,
  backendUrl,
  singleOriginEnabled,
} from '@/utils/constants';
import { cn } from '@/utils/tailwind';

import { ensureUTCFormatTimestamp } from '../../utils/ensureUTCFormatTimestamp';
import { DeleteKeyModal } from './components/DeleteKeyModal';
import { NewKeyModal } from './components/NewKeyModal';
import { compareKeys } from './utils/helpers';

// Extend dayjs with relativeTime once at module load
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - safe to call extend multiple times in dev HMR
dayjs.extend(relativeTime);

function RedirectToModels() {
  const organizationId = useOrganizationId();
  const navigate = useNavigate();
  useEffect(() => {
    navigate(
      `/${appOrganizationPath}/${organizationId}/${appSettingsPath}/models`,
      { replace: true }
    );
  }, [navigate, organizationId]);
  return null;
}

// New component to display the expiry badge using dayjs
function ExpiresAtBadge({ expiresAt }: { expiresAt: string }) {
  let date = dayjs(expiresAt);
  if (!date.isValid() && !expiresAt.endsWith('Z')) {
    date = dayjs(`${expiresAt}Z`);
  }

  if (!date.isValid()) {
    return <Badge color="secondary">Invalid date</Badge>;
  }

  const now = dayjs();
  const diffHours = date.diff(now, 'hour', true); // Fractional hours difference

  const isExpired = diffHours <= 0;
  const isUrgent = diffHours > 0 && diffHours <= 24;
  const displayText = isExpired ? 'Expired' : date.fromNow(true); // e.g., "3 days", "5 hours"

  return (
    <Badge color={isExpired ? 'error' : isUrgent ? 'warning' : 'secondary'}>
      {displayText}
    </Badge>
  );
}

function APIKeysTable(props: {
  serviceKeys: ApiKeySchema[];
  patKeys: ApiKeySchema[];
  onSuccessRef: React.MutableRefObject<(type: 'pat' | 'service') => void>;
  tab: 'service' | 'pat';
  setTab: (tab: 'service' | 'pat') => void;
}) {
  const { authorize } = usePermissions();
  const [keyToBeDeleted, setKeyToBeDeleted] = useState<string | null>(null);
  const [hoveredWorkspaceKey, setHoveredWorkspaceKey] = useState<string | null>(
    null
  );

  const values =
    props.tab === 'service'
      ? props.serviceKeys.sort(compareKeys)
      : props.tab === 'pat'
      ? props.patKeys.sort(compareKeys)
      : [...props.patKeys, ...props.serviceKeys].sort(compareKeys);

  const keyTypeMap = useMemo(() => {
    const retval: Record<string, 'pat' | 'service'> = {};
    props.patKeys.forEach((key) => (retval[key.id] = 'pat'));
    props.serviceKeys.forEach((key) => (retval[key.id] = 'service'));
    return retval;
  }, [props.serviceKeys, props.patKeys]);

  return (
    <>
      <div className="flex flex-col items-stretch rounded-lg border border-secondary">
        <div
          className={`grid ${
            props.tab !== 'pat'
              ? 'grid-cols-[1fr,1fr,2fr,1fr,1fr,1fr,auto]'
              : 'grid grid-cols-[1fr,1fr,2fr,1fr,1fr,auto]'
          } items-stretch rounded-lg text-sm`}
        >
          <div className="flex h-12 items-center bg-secondary px-5 font-semibold text-tertiary">
            Key
          </div>
          <div className="flex h-12 items-center bg-secondary px-5 font-semibold text-tertiary">
            Expires
          </div>
          <div className="flex h-12 items-center bg-secondary px-5 font-semibold text-tertiary">
            Description
          </div>
          {props.tab !== 'pat' && (
            <div className="flex h-12 items-center bg-secondary px-5 font-semibold text-tertiary">
              Workspaces
            </div>
          )}
          <div className="flex h-12 min-w-0 items-center whitespace-nowrap bg-secondary px-5 font-semibold text-tertiary">
            Created At
          </div>
          <div className="flex h-12 min-w-0 items-center whitespace-nowrap bg-secondary px-5 font-semibold text-tertiary">
            Last Used At
          </div>
          <div className="flex h-12 items-center bg-secondary px-5 font-semibold text-tertiary">
            Actions
          </div>
          {values?.map((key) => (
            <Fragment key={key.id}>
              <div className="flex h-14 items-center gap-2 border-t border-secondary px-5 py-2">
                <code className="font-mono">{key.short_key}</code>
              </div>
              <div className="flex h-14 items-center border-t border-secondary px-5 py-2">
                {key.expires_at ? (
                  <ExpiresAtBadge expiresAt={key.expires_at} />
                ) : (
                  <Badge color="secondary">Never</Badge>
                )}
              </div>
              <div className="flex h-14 min-w-0 items-center border-t border-secondary px-5 py-2 text-left">
                <span className="shrink overflow-hidden text-ellipsis whitespace-nowrap">
                  {key.description}
                </span>
              </div>
              {keyTypeMap[key.id] === 'service' && (
                <div className="flex h-14 items-center border-t border-secondary px-5 py-2">
                  <div
                    className="relative"
                    onMouseEnter={() => setHoveredWorkspaceKey(key.id)}
                    onMouseLeave={() => setHoveredWorkspaceKey(null)}
                  >
                    <Badge
                      color="secondary"
                      rounded="sm"
                      className="cursor-pointer transition-colors hover:bg-tertiary"
                    >
                      {String(key.workspace_names?.length || 0)}
                    </Badge>

                    {hoveredWorkspaceKey === key.id &&
                      key.workspace_names &&
                      key.workspace_names.length > 0 && (
                        <div className="absolute left-full top-0 z-20 ml-2 w-64 rounded-md border border-secondary bg-background p-3 shadow-lg">
                          <div className="mb-1 text-sm font-medium text-tertiary">
                            Workspaces
                          </div>
                          <div className="mb-2 text-xs text-tertiary">
                            This key is scoped to the following workspaces
                          </div>
                          <div className="space-y-1">
                            {key.workspace_names.map((workspace, index) => (
                              <div
                                key={index}
                                className="truncate text-sm text-primary"
                                title={workspace}
                              >
                                {workspace}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              )}
              <div className="flex h-14 min-w-0 items-center border-t border-secondary px-5 py-2">
                {key.created_at != null && (
                  <span className="shrink overflow-hidden text-ellipsis whitespace-nowrap">
                    {new Date(
                      ensureUTCFormatTimestamp(key.created_at)
                    ).toLocaleString()}
                  </span>
                )}
              </div>
              <div className="flex h-14 min-w-0 items-center border-t border-secondary px-5 py-2">
                {key.last_used_at != null && (
                  <span className="shrink overflow-hidden text-ellipsis whitespace-nowrap">
                    {new Date(
                      ensureUTCFormatTimestamp(key.last_used_at)
                    ).toLocaleString()}
                  </span>
                )}
              </div>
              <div className="flex h-14 items-center justify-end border-t border-secondary px-5 py-2">
                {keyTypeMap[key.id] == 'pat' ||
                authorize('workspaces:manage') ? (
                  <Button
                    type="button"
                    variant="plain"
                    size="sm"
                    aria-label="delete"
                    onClick={() => setKeyToBeDeleted(key.id)}
                    className="p-1"
                  >
                    <TrashIcon className="h-5 w-5 text-tertiary" />
                  </Button>
                ) : null}
              </div>
            </Fragment>
          ))}
        </div>

        {keyToBeDeleted && (
          <DeleteKeyModal
            isOpen
            type={keyTypeMap[keyToBeDeleted] ?? 'service'}
            doClose={() => setKeyToBeDeleted(null)}
            keyId={keyToBeDeleted}
          />
        )}
      </div>
    </>
  );
}

export function OrganizationApiKeys() {
  const { needsAuth } = useAuth();
  const organization = useCurrentOrganization();
  const { authorize } = usePermissions();

  const [newKey, setNewKey] = useState<string | null>(null);
  const [isInfoHovered, setIsInfoHovered] = useState(false);

  const onSuccessRef = useRef<(type: 'pat' | 'service') => void>(() => void 0);

  const serviceKeys = useOrgScopedApiKeys();
  const patKeys = useOrgScopedPatKeys();

  const isLoading =
    serviceKeys.isLoading || patKeys.isLoading || organization.isLoading;

  const [tab, setTab] = useState<'service' | 'pat'>(() => {
    if (
      serviceKeys.data &&
      serviceKeys.data.length > 0 &&
      patKeys.data &&
      patKeys.data.length === 0
    )
      return 'service';
    return 'pat';
  });

  if (!needsAuth) {
    return <RedirectToModels />;
  }

  return (
    <div className="flex flex-col gap-4 overflow-visible">
      <div className="flex items-center gap-1">
        <div className="flex flex-grow flex-col gap-1">
          <div className="flex items-center gap-2 py-4">
            <Key01Icon className="h-6 w-6 text-primary" />
            <h3 className={cn('text-3xl')}>API Keys</h3>
            <div className="relative">
              <InfoCircleIcon
                className="h-5 w-5 cursor-pointer text-tertiary transition-colors hover:text-secondary"
                onMouseEnter={() => setIsInfoHovered(true)}
                onMouseLeave={() => setIsInfoHovered(false)}
              />
              {isInfoHovered && (
                <div className="absolute left-full top-0 z-20 ml-2 w-64 rounded-md border border-secondary bg-background p-3 shadow-lg">
                  <div className="mb-1 text-sm font-medium text-tertiary">
                    API Keys
                  </div>
                  <div className="text-xs text-tertiary">
                    You can only access an API key when you first create it. If
                    you lost one, you will need to create a new one.
                  </div>
                </div>
              )}
            </div>
            <div className="ml-4 flex items-center gap-4">
              <GroupedTabs
                value={tab}
                onChange={setTab}
                options={[
                  { value: 'pat', display: 'Personal' },
                  { value: 'service', display: 'Service' },
                ]}
                size="md"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <a
            href={`${backendUrl}${
              singleOriginEnabled === '1' ? '/api' : ''
            }/redoc`}
            target="_blank"
            rel="noreferrer"
            className="inline_block"
          >
            <Button
              size="md"
              variant="outlined"
              color="secondary"
              leftDecorator={File02Icon}
            >
              API docs
            </Button>
          </a>

          <CreateKeyModal
            isWorkspaceAdmin={authorize('workspaces:manage') || false}
            onSuccess={(type, data) => {
              onSuccessRef.current(type);
              setNewKey(data?.key ?? null);
            }}
            onTabChange={(tab) => {
              if (tab === 'pat' || tab === 'service') {
                setTab(tab);
              }
            }}
          />
        </div>
      </div>

      {!isLoading ? (
        <APIKeysTable
          serviceKeys={serviceKeys.data ?? []}
          patKeys={patKeys.data ?? []}
          onSuccessRef={onSuccessRef}
          tab={tab}
          setTab={setTab}
        />
      ) : (
        <div>
          <LinearProgress />
        </div>
      )}

      {newKey && (
        <NewKeyModal isOpen doClose={() => setNewKey(null)} apiKey={newKey} />
      )}
    </div>
  );
}
